// "Needs attention" card on the admin dashboard (enquiries plan §10): one line per enabled flag
// with its count, each linking to the filtered enquiries list. Hidden when the server sent no flags
// (`null`: the caller may not read enquiries) or when every flag is switched off. Loading, error and "nothing needs attention" are
// three different states.

import { Alert, Box, Card, CardContent, CardHeader, Link, Skeleton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { EnquiryFlagKey } from '../../api/enquiry.api';
import { ENQUIRY_FLAG_KEY_LIST, ENQUIRY_FLAG_LABELS } from '../../utils/enquiryFlags';

interface Props {
  /** `undefined` while the stats load; `null` = not for this user. */
  flags: Record<EnquiryFlagKey, number | null> | null | undefined;
  isLoading?: boolean;
  isError?: boolean;
}

function flagLines(flags: Props['flags']) {
  return flags
    ? ENQUIRY_FLAG_KEY_LIST.filter((k) => flags[k] !== null && flags[k] !== undefined).map((k) => ({ key: k, count: flags[k] as number }))
    : [];
}

/**
 * Whether the card renders at all: the ONE rule, so the dashboard's spacing wrapper can follow it
 * (whole-feature review N1) instead of leaving an empty margin box when the card hides.
 */
export function enquiryAttentionCardVisible({ flags, isLoading = false, isError = false }: Props): boolean {
  if (!isLoading && !isError && (flags === null || flags === undefined)) return false;
  // Every flag switched off (all counts null): there is nothing to show and nothing is being checked,
  // so the card is hidden rather than claiming "Nothing needs attention" (review L3).
  if (!isLoading && flags && flagLines(flags).length === 0) return false;
  return true;
}

export default function EnquiryAttentionCard({ flags, isLoading = false, isError = false }: Props) {
  if (!enquiryAttentionCardVisible({ flags, isLoading, isError })) return null;

  const lines = flagLines(flags);
  // A failed background refetch keeps the counts already loaded: the error shows only with no data (L4).
  const showError = isError && !flags;
  const needing = lines.filter((l) => l.count > 0);

  return (
    <Card sx={{ height: '100%' }} data-testid="enquiry-attention-card">
      <CardHeader title={<Typography variant="h6">Enquiries needing attention</Typography>} />
      <CardContent sx={{ minWidth: 0 }}>
        {isLoading ? (
          <Box>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="65%" />
          </Box>
        ) : showError ? (
          <Alert severity="error">Could not load the enquiry flags. Please try again.</Alert>
        ) : needing.length === 0 ? (
          <Typography color="text.secondary">Nothing needs attention</Typography>
        ) : (
          <Stack spacing={1}>
            {needing.map(({ key, count }) => (
              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                <Link component={RouterLink} to={`enquiries?flag=${key}`} underline="hover" sx={{ minWidth: 0 }}>
                  {ENQUIRY_FLAG_LABELS[key]}
                </Link>
                <Typography fontWeight={600}>{count}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
